/**
 * Copy for the first-login wizard. Same flat-key convention as the tour copy
 * (`src/features/onboarding/tour-copy.ts`): Indonesian is authored first, zh
 * second, and English falls back to Indonesian until it is authored.
 *
 * Callers resolve at render, so switching the language on step 0 re-renders
 * every string on screen.
 */
import { DEFAULT_LANGUAGE, type LanguageCode } from '@constants/translations';

type CopyDict = Record<string, string>;

const id: CopyDict = {
  'rail.welcome': 'Selamat datang',
  'rail.note.default': 'Semua langkah bisa dilewati. Anda bisa mengisinya kapan saja dari menu Master.',
  'rail.note.appearance': 'Semua langkah bisa dilewati. Tampilan bisa diubah kapan saja di Pengaturan.',
  'rail.note.master': 'Semua langkah bisa dilewati. Data PPJK bisa dilihat dan diubah di menu Master.',
  'rail.note.private': 'Data ini hanya dipakai oleh PPJK Anda dan tidak dibagikan ke pihak lain.',
  'rail.note.consignee':
    'Data consignee hanya bisa dilihat oleh pengguna PPJK Anda. Tidak dibagikan ke PPJK lain dan hanya dipakai untuk mengisi dokumen Anda.',

  'step.tampilan.title': 'Tampilan',
  'step.tampilan.sub': 'Tema, bahasa, ukuran teks',
  'step.pernyataan.title': 'Pernyataan',
  'step.pernyataan.sub': 'Penanda tangan dokumen',
  'step.consignee.title': 'Data Consignee',
  'step.consignee.sub': 'Unggah dari Excel',
  'step.film.filledNow.title': 'Di mana mengubahnya',
  'step.film.teamFilled.title': 'Data PPJK sudah siap',
  'step.film.masterInfo.title': 'Semua ada di menu Master',
  'step.film.appearance.title': 'Atur tampilan',
  'step.film.sub': 'Tur singkat',

  'tampilan.heading': 'Atur tampilan sesuai kenyamanan Anda',
  'tampilan.lead': 'Pilihan ini hanya berlaku untuk akun Anda dan bisa diubah kapan saja di Pengaturan.',

  'pernyataan.heading': 'Siapa yang menandatangani dokumen PIB/PEB Anda?',
  'pernyataan.lead': 'Diisi otomatis ke bagian Pernyataan setiap dokumen baru. Bisa diubah per dokumen.',
  'pernyataan.field.place': 'Tempat (kota)',
  'pernyataan.field.name': 'Nama penanda tangan',
  'pernyataan.field.position': 'Jabatan',
  'pernyataan.placeholder.place': 'JAKARTA',
  'pernyataan.placeholder.name': 'Nama sesuai dokumen',
  'pernyataan.placeholder.position': 'Misalnya DIREKTUR',
  'pernyataan.note.title': 'Dipakai bersama satu PPJK.',
  'pernyataan.note.body':
    'Jika rekan Anda mengisinya lebih dulu, langkah ini tidak akan muncul lagi. Nilai terakhir yang disimpan yang dipakai.',
  'pernyataan.required': 'Isi ketiga kolom untuk menyimpan, atau lewati dulu.',
  'pernyataan.saveFailed': 'Pernyataan belum tersimpan. Coba lagi atau lewati dulu.',
  'pernyataan.utamaHint': 'Disimpan sebagai Pernyataan utama. Pernyataan lain bisa ditambahkan di Master › Pernyataan.',

  'consignee.heading': 'Daftar consignee Anda',
  'consignee.lead': 'Isi sekali, pakai berulang. Kosongkan yang tidak tahu, hanya Nama dan NPWP yang wajib.',
  'consignee.card.template.title': '1. Unduh template',
  'consignee.card.template.body': 'Excel dengan kolom siap isi, contoh baris, dan pilihan dropdown.',
  'consignee.card.template.button': 'Unduh Template_Consignee_JARVIS.xlsx',
  'consignee.card.upload.title': '2. Unggah yang sudah diisi',
  'consignee.downloadFailed': 'Template gagal diunduh. Coba lagi.',
  'consignee.imported': '{{n}} consignee tersimpan. Anda bisa lanjut.',

  'film.filledNow.heading': 'Di mana mengubahnya nanti',
  'film.filledNow.lead': 'Semua yang Anda isi tadi ada di menu Master, dan tampilan ada di Pengaturan.',
  'film.filledNow.caption':
    'Sidebar, lalu Master, lalu Pernyataan dan Data Consignee. Tampilan bisa diubah lagi di Pengaturan.',
  'film.teamFilled.heading': 'Data PPJK Anda sudah siap',
  'film.teamFilled.lead':
    'Tim Anda sudah mengisi Pernyataan dan Data Consignee. Anda bisa melihat dan mengubahnya di menu Master, dan mengatur tampilan di Pengaturan.',
  'film.teamFilled.caption':
    'Sidebar, lalu Master, lalu Pernyataan dan Data Consignee. Tampilan bisa diubah lagi di Pengaturan.',
  'film.masterInfo.heading': 'Semua ada di menu Master',
  'film.masterInfo.lead':
    'Pernyataan dan Data Consignee PPJK Anda bisa dilihat dan diubah di menu Master, dan tampilan diatur di Pengaturan.',
  'film.masterInfo.caption':
    'Sidebar, lalu Master, lalu Pernyataan dan Data Consignee. Tampilan bisa diubah lagi di Pengaturan.',
  'film.appearance.heading': 'Atur tampilan kapan saja',
  'film.appearance.lead': 'Tema, bahasa, dan ukuran teks bisa diubah lagi di Pengaturan.',
  'film.appearance.caption': 'Sidebar, lalu Pengaturan di bagian bawah.',
  'film.replay': 'Putar ulang',

  'colleague.statement.title': 'Pernyataan sudah diisi oleh rekan Anda',
  'colleague.statement.keep': 'Tetap ubah',
  'colleague.consignee.title': 'Data Consignee sudah diisi oleh rekan Anda ({{n}} data)',
  'colleague.consignee.keep': 'Tetap unggah',
  'colleague.continue': 'Lanjut',

  'footer.later': 'Lanjutkan nanti',
  'footer.back': 'Kembali',
  'footer.skip': 'Lewati',
  'footer.saveNext': 'Simpan & lanjut',
  'footer.next': 'Lanjut',
  'footer.finish': 'Selesai',
  'footer.stepOf': 'Langkah {{current}} dari {{total}}',
};

const zh: CopyDict = {
  'rail.welcome': '欢迎',
  'rail.note.default': '每一步都可以跳过。之后随时可以在 Master 菜单里补填。',
  'rail.note.appearance': '每一步都可以跳过。外观随时可以在“设置”里修改。',
  'rail.note.master': '每一步都可以跳过。PPJK 数据可以在 Master 菜单中查看和修改。',
  'rail.note.private': '这些数据只供您的 PPJK 使用，不会与任何第三方共享。',
  'rail.note.consignee': '收货人数据只有您 PPJK 的用户可见。不会共享给其他 PPJK，只用于填写您的单证。',

  'step.tampilan.title': '外观',
  'step.tampilan.sub': '主题、语言、字号',
  'step.pernyataan.title': '声明 (Pernyataan)',
  'step.pernyataan.sub': '单证签署人',
  'step.consignee.title': '收货人数据',
  'step.consignee.sub': '从 Excel 上传',
  'step.film.filledNow.title': '之后在哪里修改',
  'step.film.teamFilled.title': 'PPJK 数据已就绪',
  'step.film.masterInfo.title': '都在 Master 菜单里',
  'step.film.appearance.title': '调整外观',
  'step.film.sub': '简短导览',

  'tampilan.heading': '把界面调到您看着舒服',
  'tampilan.lead': '这些选择只影响您的账号，随时可以在“设置”里更改。',

  'pernyataan.heading': '谁来签署您的 PIB/PEB 单证？',
  'pernyataan.lead': '会自动填入每份新单证的 Pernyataan 栏，也可以按单证单独修改。',
  'pernyataan.field.place': '地点（城市）',
  'pernyataan.field.name': '签署人姓名',
  'pernyataan.field.position': '职务',
  'pernyataan.placeholder.place': 'JAKARTA',
  'pernyataan.placeholder.name': '与单证一致的姓名',
  'pernyataan.placeholder.position': '例如 DIREKTUR',
  'pernyataan.note.title': '整个 PPJK 共用。',
  'pernyataan.note.body': '如果同事已经先填好，这一步就不会再出现。以最后一次保存的值为准。',
  'pernyataan.required': '三项都填好才能保存，或者先跳过。',
  'pernyataan.saveFailed': '声明尚未保存。请重试，或先跳过。',
  'pernyataan.utamaHint': '保存为默认声明。其他声明可在 主数据 › 声明 中添加。',

  'consignee.heading': '您的收货人名单',
  'consignee.lead': '填一次，反复用。不清楚的留空，只有名称和 NPWP 必填。',
  'consignee.card.template.title': '1. 下载模板',
  'consignee.card.template.body': 'Excel 已备好列名、示例行和下拉选项。',
  'consignee.card.template.button': '下载 Template_Consignee_JARVIS.xlsx',
  'consignee.card.upload.title': '2. 上传填好的文件',
  'consignee.downloadFailed': '模板下载失败，请重试。',
  'consignee.imported': '已保存 {{n}} 个收货人。可以继续。',

  'film.filledNow.heading': '之后在哪里修改',
  'film.filledNow.lead': '刚才填的内容都在 Master 菜单里，外观设置在“设置”里。',
  'film.filledNow.caption': '侧边栏，然后 Master，然后 Pernyataan 和收货人数据。外观随时可以在“设置”里再改。',
  'film.teamFilled.heading': '您的 PPJK 数据已就绪',
  'film.teamFilled.lead':
    '您的团队已经填好 Pernyataan 和收货人数据。您可以在 Master 菜单中查看和修改，并在“设置”里调整外观。',
  'film.teamFilled.caption': '侧边栏，然后 Master，然后 Pernyataan 和收货人数据。外观随时可以在“设置”里再改。',
  'film.masterInfo.heading': '都在 Master 菜单里',
  'film.masterInfo.lead': '您 PPJK 的 Pernyataan 和收货人数据可以在 Master 菜单中查看和修改，外观在“设置”里调整。',
  'film.masterInfo.caption': '侧边栏，然后 Master，然后 Pernyataan 和收货人数据。外观随时可以在“设置”里再改。',
  'film.appearance.heading': '随时调整外观',
  'film.appearance.lead': '主题、语言和字号随时可以在“设置”里再改。',
  'film.appearance.caption': '侧边栏，然后底部的“设置”。',
  'film.replay': '重播',

  'colleague.statement.title': '您的同事已经填好了声明 (Pernyataan)',
  'colleague.statement.keep': '仍要修改',
  'colleague.consignee.title': '您的同事已经填好了收货人数据（{{n}} 条）',
  'colleague.consignee.keep': '仍要上传',
  'colleague.continue': '继续',

  'footer.later': '稍后继续',
  'footer.back': '上一步',
  'footer.skip': '跳过',
  'footer.saveNext': '保存并继续',
  'footer.next': '继续',
  'footer.finish': '完成',
  'footer.stepOf': '第 {{current}} 步，共 {{total}} 步',
};

const FIRST_LOGIN_COPY: Record<LanguageCode, CopyDict> = { id, zh, en: {} };

export const resolveFirstLoginCopy = (
  language: LanguageCode,
  key: string,
  vars?: Record<string, string | number>
): string => {
  const raw = FIRST_LOGIN_COPY[language]?.[key] ?? FIRST_LOGIN_COPY[DEFAULT_LANGUAGE][key] ?? key;
  if (!vars) {
    return raw;
  }
  return Object.entries(vars).reduce((text, [name, value]) => text.split(`{{${name}}}`).join(String(value)), raw);
};

/** Test-only view of the raw dictionaries. */
export const __FIRST_LOGIN_COPY = FIRST_LOGIN_COPY;
